import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const DIMS = [
  { key: 'profile',  name: 'Profile Quality',  weight: 25, color: 'hsl(226 100% 65%)', detail: 'Verified credentials, complete service area, transparent pricing.' },
  { key: 'perf',     name: 'Job Performance',  weight: 25, color: 'hsl(150 80% 55%)',  detail: 'Completion rate, on-time service, accepted-quote follow-through.' },
  { key: 'response', name: 'Response Rate',    weight: 20, color: 'hsl(38 95% 60%)',   detail: 'Time-to-quote. Sub-24h is the floor. Sub-2h is elite.' },
  { key: 'reviews',  name: 'Client Reviews',   weight: 20, color: 'hsl(320 90% 65%)',  detail: 'Verified post-service ratings, written feedback, dispute history.' },
  { key: 'area',     name: 'Area Confidence',  weight: 10, color: 'hsl(270 95% 70%)',  detail: 'Tenure on Servio, geographic depth, consistency over time.' },
];

const SCORES = { profile: 95, perf: 88, response: 90, reviews: 94, area: 85 };

const RADIUS = 110;
const CX = 150;
const CY = 150;

function point(angle, dist) {
  const rad = (angle - 90) * (Math.PI / 180);
  return [CX + Math.cos(rad) * dist, CY + Math.sin(rad) * dist];
}

/**
 * Interactive 5-dimensional trust-score radar.
 * Hover/tap any axis label or polygon vertex to focus that dimension —
 * others dim, the composite score pulses, and a detail card slides in.
 */
export function TrustScoreRadar() {
  const [hover, setHover] = useState(null);

  const composite = Math.round(
    DIMS.reduce((sum, d) => sum + (SCORES[d.key] * d.weight) / 100, 0)
  );

  const verts = DIMS.map((d, i) => {
    const angle = (i / DIMS.length) * 360;
    const value = SCORES[d.key] / 100;
    return { angle, value, dim: d, p: point(angle, RADIUS * value) };
  });

  const polygonPath = verts.map((v) => v.p.join(',')).join(' ');

  return (
    <div className="relative flex flex-col lg:flex-row items-center gap-8" data-testid="trust-radar">
      <svg viewBox="0 0 300 300" className="w-full max-w-[340px] shrink-0">
        <defs>
          <radialGradient id="radFill" cx="0.5" cy="0.5" r="0.6">
            <stop offset="0%" stopColor="hsl(226, 100%, 65%)" stopOpacity="0.55" />
            <stop offset="100%" stopColor="hsl(270, 100%, 65%)" stopOpacity="0.10" />
          </radialGradient>
          <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="4" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Concentric rings */}
        {[0.25, 0.5, 0.75, 1].map((r, i) => (
          <circle key={i} cx={CX} cy={CY} r={RADIUS * r} fill="none" stroke="hsl(220 30% 20%)" strokeWidth="1" strokeDasharray={i === 3 ? '0' : '2 4'} />
        ))}

        {/* Axes */}
        {DIMS.map((d, i) => {
          const angle = (i / DIMS.length) * 360;
          const [x, y] = point(angle, RADIUS);
          return (
            <line key={i} x1={CX} y1={CY} x2={x} y2={y} stroke="hsl(220 30% 22%)" strokeWidth="1" />
          );
        })}

        {/* Filled polygon */}
        <motion.polygon
          points={polygonPath}
          fill="url(#radFill)"
          stroke="hsl(226, 100%, 65%)"
          strokeWidth="2"
          filter="url(#glow)"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
          style={{ transformOrigin: `${CX}px ${CY}px` }}
        />

        {/* Vertices + labels */}
        {verts.map((v, i) => {
          const [lx, ly] = point(v.angle, RADIUS + 26);
          const isHover = hover === v.dim.key;
          return (
            <g key={i} onMouseEnter={() => setHover(v.dim.key)} onMouseLeave={() => setHover(null)} className="cursor-pointer">
              <circle cx={v.p[0]} cy={v.p[1]} r={isHover ? 8 : 5} fill={v.dim.color} stroke="hsl(220 40% 5%)" strokeWidth="2" />
              {isHover && <circle cx={v.p[0]} cy={v.p[1]} r="14" fill={v.dim.color} opacity="0.25" />}
              <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" className="fill-[hsl(220,10%,80%)] text-[11px] font-mono uppercase tracking-[0.14em]">
                {v.dim.name.split(' ')[0]}
              </text>
            </g>
          );
        })}

        {/* Center composite score */}
        <text x={CX} y={CY - 4} textAnchor="middle" className="fill-white" style={{ fontSize: 38, fontWeight: 800, letterSpacing: '-0.04em', fontFamily: 'Geist' }}>
          {composite}
        </text>
        <text x={CX} y={CY + 22} textAnchor="middle" className="fill-[hsl(220,10%,55%)]" style={{ fontSize: 9, letterSpacing: '0.22em', fontFamily: 'Geist Mono' }}>
          SERVIO SCORE
        </text>
      </svg>

      {/* Right detail */}
      <div className="flex-1 w-full">
        <div className="font-mono text-[10px] tracking-[0.22em] text-[hsl(150,80%,55%)] uppercase mb-2">Hover any axis</div>
        <AnimatePresence mode="wait">
          {hover ? (
            <motion.div
              key={hover}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              transition={{ duration: 0.25 }}
            >
              {DIMS.filter((d) => d.key === hover).map((d) => (
                <div key={d.key}>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="inline-block w-3 h-3 rounded-full" style={{ background: d.color }} />
                    <h3 className="text-2xl font-bold tracking-tight">{d.name}</h3>
                    <span className="ml-auto font-mono text-xl font-bold" style={{ color: d.color }}>{SCORES[d.key]}</span>
                  </div>
                  <p className="text-[hsl(220,12%,70%)] text-sm leading-relaxed">{d.detail}</p>
                  <div className="mt-3 text-xs font-mono text-[hsl(220,12%,55%)]">
                    Weight in composite: {d.weight}%
                  </div>
                </div>
              ))}
            </motion.div>
          ) : (
            <motion.div
              key="default"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
            >
              <h3 className="text-2xl font-bold tracking-tight mb-2">A five-dimensional read on trust.</h3>
              <p className="text-[hsl(220,12%,70%)] text-sm leading-relaxed max-w-md">
                The Servio Score™ is a weighted composite. No directory ranks by who paid the most. Hover an axis to see how each dimension factors in.
              </p>
              <div className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 max-w-md">
                {DIMS.map((d) => (
                  <div key={d.key} className="flex items-center justify-between text-sm border-b border-[hsl(220,30%,18%)] pb-1.5">
                    <span className="flex items-center gap-2 text-[hsl(220,12%,80%)]">
                      <span className="inline-block w-2 h-2 rounded-full" style={{ background: d.color }} />
                      {d.name}
                    </span>
                    <span className="font-mono text-xs text-[hsl(220,12%,55%)]">{d.weight}%</span>
                  </div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default TrustScoreRadar;
